/**
 * @file pages/admin/BlogEditor.jsx
 * @description Admin blog editor with Rich Text Editor (React Quill),
 * featured image upload, SEO fields, category/tags, and publish/draft toggle.
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import ImageResize from 'quill-image-resize-module-react';
import { FiArrowLeft, FiSave, FiImage, FiX, FiGrid, FiTrash2 } from 'react-icons/fi';
import { createBlog, updateBlog, getBlog, uploadBlogImage } from '../../services/api';
import PhotoGridModal from './PhotoGridModal';

// --- Quill Module Registration (runs once at module level) ---
Quill.register('modules/imageResize', ImageResize);

// Extend Image format to preserve width, height, style attributes
const BaseImageFormat = Quill.import('formats/image');
const IMAGE_ATTRS = ['alt', 'height', 'width', 'style'];
class ImageFormat extends BaseImageFormat {
  static formats(domNode) {
    return IMAGE_ATTRS.reduce((formats, attr) => {
      if (domNode.hasAttribute(attr)) formats[attr] = domNode.getAttribute(attr);
      return formats;
    }, {});
  }
  format(name, value) {
    if (IMAGE_ATTRS.includes(name)) {
      value ? this.domNode.setAttribute(name, value) : this.domNode.removeAttribute(name);
    } else {
      super.format(name, value);
    }
  }
}
Quill.register(ImageFormat, true);

const CATEGORIES = [
  'Travel Guide',
  'Destination',
  'Adventure',
  'Culture & Heritage',
  'Food & Cuisine',
  'Tips & Hacks',
  'Budget Travel',
  'Luxury Travel',
  'Solo Travel',
  'Family Travel',
  'Wildlife & Nature',
  'Spiritual Journey'
];

const BlogEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const quillRef = useRef(null);
  const featuredImageInputRef = useRef(null);
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    category: 'Travel Guide',
    tags: '',
    publishStatus: 'draft',
    seoTitle: '',
    seoDescription: '',
    authorName: '',
    authorPhoto: '',
  });
  const [featuredImage, setFeaturedImage] = useState(null);
  const [featuredImagePreview, setFeaturedImagePreview] = useState('');
  const [showSeoPanel, setShowSeoPanel] = useState(false);
  const [showGridModal, setShowGridModal] = useState(false);

  // Auto-generate slug preview
  const slugPreview = formData.title
    ? formData.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
    : '';

  // Load existing blog for editing
  useEffect(() => {
    if (isEditing) {
      const loadBlog = async () => {
        try {
          setLoading(true);
          const response = await getBlog(id);
          const blog = response.data?.data?.blog || response.data?.blog;
          if (blog) {
            setFormData({
              title: blog.title || '',
              content: blog.content || '',
              excerpt: blog.excerpt || '',
              category: blog.category || 'Travel Guide',
              tags: Array.isArray(blog.tags) ? blog.tags.join(', ') : (blog.tags || ''),
              publishStatus: blog.publishStatus || 'draft',
              seoTitle: blog.seoTitle || '',
              seoDescription: blog.seoDescription || '',
              authorName: blog.authorName || (typeof blog.author === 'object' ? blog.author?.name : '') || '',
              authorPhoto: blog.authorPhoto || (typeof blog.author === 'object' ? blog.author?.photoURL : '') || '',
            });
            if (blog.featuredImage?.url) {
              setFeaturedImagePreview(blog.featuredImage.url);
            }
          }
        } catch (error) {
          toast.error('Failed to load blog post');
          navigate('/admin/blog');
        } finally {
          setLoading(false);
        }
      };
      loadBlog();
    }
  }, [id, isEditing, navigate]);

  // Quill image upload handler
  const imageHandler = () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();
    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;
      try {
        toast.info('Uploading image...');
        const fd = new FormData();
        fd.append('image', file);
        const response = await uploadBlogImage(fd);
        const url = response.data?.data?.url || response.data?.url;
        if (url && quillRef.current) {
          const editor = quillRef.current.getEditor();
          editor.focus();
          const range = editor.getSelection();
          const index = range ? range.index : editor.getLength() - 1;
          editor.insertEmbed(index, 'image', url, 'user');
          editor.setSelection(index + 1, 0, 'user');
          toast.success('Image uploaded & inserted!');
        } else {
          console.error('No URL in response or no editor ref:', response.data);
          toast.error('Upload succeeded but no image URL returned');
        }
      } catch (error) {
        console.error('Image upload error:', error.response?.data || error.message);
        const msg = error.response?.data?.message || error.message;
        if (error.response?.status === 401 || error.response?.status === 403) {
          toast.error('Authentication failed. Please log out and log back in.');
        } else {
          toast.error('Failed to upload image: ' + msg);
        }
      }
    };
  };

  // Attach onerror to images in editor so truly broken ones get hidden
  useEffect(() => {
    if (!quillRef.current) return;
    const editorRoot = quillRef.current.getEditor().root;
    const attachErrorHandler = (img) => {
      if (img._errorHandled) return;
      img._errorHandled = true;
      img.onerror = () => {
        // Only hide if the src is empty or truly failed
        if (!img.src || img.src === window.location.href) {
          img.style.display = 'none';
        } else {
          img.style.opacity = '0.3';
          img.title = 'Image failed to load';
        }
      };
    };
    // Attach to existing images after Quill renders
    const timer = setTimeout(() => {
      editorRoot.querySelectorAll('img').forEach(attachErrorHandler);
    }, 1000);
    // Watch for newly inserted images
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((m) => {
        m.addedNodes.forEach((node) => {
          if (node.tagName === 'IMG') attachErrorHandler(node);
          if (node.querySelectorAll) node.querySelectorAll('img').forEach(attachErrorHandler);
        });
      });
    });
    observer.observe(editorRoot, { childList: true, subtree: true });
    return () => { observer.disconnect(); clearTimeout(timer); };
  }, []);

  // Handle pasted images — upload to Cloudinary instead of inserting base64
  useEffect(() => {
    if (!quillRef.current) return;
    const editor = quillRef.current.getEditor();
    const handlePaste = (e) => {
      const clipboardData = e.clipboardData || window.clipboardData;
      if (!clipboardData) return;
      const items = clipboardData.items;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          e.preventDefault();
          e.stopPropagation();
          const file = items[i].getAsFile();
          if (!file) return;
          toast.info('Uploading pasted image...');
          const fd = new FormData();
          fd.append('image', file);
          uploadBlogImage(fd).then((response) => {
            const url = response.data?.data?.url;
            if (url) {
              const range = editor.getSelection(true);
              editor.insertEmbed(range ? range.index : editor.getLength(), 'image', url);
              toast.success('Image inserted!');
            } else {
              toast.error('Upload succeeded but no URL returned');
            }
          }).catch((err) => {
            toast.error('Failed to upload pasted image');
            console.error('Paste image upload error:', err);
          });
          break;
        }
      }
    };
    editor.root.addEventListener('paste', handlePaste);
    return () => editor.root.removeEventListener('paste', handlePaste);
  }, []);

  // Insert a photo grid into the editor content
  const insertPhotoGrid = useCallback((urls, cols, gridGap) => {
    if (!quillRef.current) return;
    const editor = quillRef.current.getEditor();
    const range = editor.getSelection(true);
    const imgWidth = `calc(${(100 / cols).toFixed(2)}% - ${gridGap}px)`;

    // Build grid HTML with inline styles that survive Quill serialization
    const imagesHtml = urls.map((url) =>
      `<img src="${url}" style="width:${imgWidth};display:inline-block;object-fit:cover;border-radius:8px;vertical-align:top;" />`
    ).join('');

    // Use dangerouslyPasteHTML to insert images
    editor.clipboard.dangerouslyPasteHTML(range.index, imagesHtml);

    // Apply flex container on parent via DOM
    requestAnimationFrame(() => {
      const allImgs = editor.root.querySelectorAll('img');
      const insertedImgs = Array.from(allImgs).filter(img =>
        urls.some(u => img.src === u || img.src.includes(u.split('/').pop()))
      );
      if (insertedImgs.length > 0) {
        const parentEl = insertedImgs[0].closest('p') || insertedImgs[0].parentElement;
        if (parentEl) {
          parentEl.style.cssText = `display:flex;flex-wrap:wrap;gap:${gridGap}px;margin:16px 0;`;
          parentEl.setAttribute('data-photo-grid', cols);
          insertedImgs.forEach(img => {
            img.style.width = imgWidth;
            img.style.display = 'block';
            img.style.objectFit = 'cover';
            img.style.borderRadius = '8px';
          });
        }
      }
    });

    editor.setSelection(range.index + urls.length + 1);
  }, []);

  // Remove only broken/invalid images from editor content (not working ones)
  const cleanBrokenImages = useCallback(() => {
    if (!quillRef.current) return;
    const editor = quillRef.current.getEditor();
    const editorRoot = editor.root;
    const imgs = Array.from(editorRoot.querySelectorAll('img'));
    let removed = 0;

    imgs.forEach(img => {
      const isBroken = !img.src ||
        img.src === '' ||
        img.src === window.location.href ||
        img.style.display === 'none' ||
        (img.complete && img.naturalWidth === 0 && img.src);

      if (isBroken) {
        img.remove();
        removed++;
      }
    });

    if (removed > 0) {
      // Sync the cleaned DOM back to formData
      setFormData(prev => ({ ...prev, content: editorRoot.innerHTML }));
      toast.success(`Removed ${removed} broken image(s)`);
    } else {
      toast.info('No broken images found');
    }
  }, []);

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        ['blockquote', 'code-block'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ align: [] }],
        ['link', 'image', 'video'],
        ['clean'],
      ],
      handlers: { image: imageHandler },
    },
    imageResize: {
      parchment: Quill.import('parchment'),
      modules: ['Resize', 'DisplaySize', 'Toolbar'],
    },
    clipboard: { matchVisual: false },
  }), []);

  const formats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'blockquote', 'code-block',
    'list', 'bullet', 'indent', 'align',
    'link', 'image', 'video',
    'width', 'height', 'style', 'alt',
  ];

  const handleFeaturedImageChange = (e) => {
    const file = e.target.files?.[0];
    console.log('Featured image selected:', file?.name, file?.type, file?.size);
    if (file) {
      setFeaturedImage(file);
      const reader = new FileReader();
      reader.onload = () => {
        console.log('FileReader loaded, setting preview');
        setFeaturedImagePreview(reader.result);
      };
      reader.onerror = (err) => console.error('FileReader error:', err);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (status) => {
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!formData.content.trim() || formData.content === '<p><br></p>') {
      toast.error('Content is required');
      return;
    }

    try {
      setSaving(true);
      const fd = new FormData();
      fd.append('title', formData.title);
      // Capture raw innerHTML from editor DOM to preserve all inline styles (flex grids, image sizes, etc.)
      const rawContent = quillRef.current
        ? quillRef.current.getEditor().root.innerHTML
        : formData.content;
      fd.append('content', rawContent);
      fd.append('excerpt', formData.excerpt || formData.content.replace(/<[^>]*>/g, '').substring(0, 200));
      fd.append('category', formData.category);
      fd.append('tags', formData.tags);
      fd.append('publishStatus', status || formData.publishStatus);
      fd.append('seoTitle', formData.seoTitle || formData.title.substring(0, 70));
      fd.append('seoDescription', formData.seoDescription || formData.excerpt?.substring(0, 160) || '');
      if (formData.authorName) fd.append('authorName', formData.authorName);
      if (formData.authorPhoto) fd.append('authorPhoto', formData.authorPhoto);

      if (featuredImage) {
        fd.append('featuredImage', featuredImage);
      }

      if (isEditing) {
        await updateBlog(id, fd);
        toast.success('Post updated successfully!');
      } else {
        await createBlog(fd);
        toast.success('Post created successfully!');
      }
      navigate('/admin/blog');
    } catch (error) {
      console.error('Error saving blog:', error);
      toast.error(error.response?.data?.message || 'Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#243185]"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Top Bar */}
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
        <button
          onClick={() => navigate('/admin/blog')}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <FiArrowLeft className="w-5 h-5" />
          <span className="font-medium">Back to Blog Manager</span>
        </button>
        <div className="flex items-center gap-3">
          <button
            onClick={() => handleSubmit('draft')}
            disabled={saving}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
          >
            Save Draft
          </button>
          <button
            onClick={() => handleSubmit('published')}
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2 bg-[#243185] text-white rounded-lg hover:bg-[#1a2460] transition-colors font-medium disabled:opacity-50 shadow-sm"
          >
            <FiSave className="w-4 h-4" />
            {saving ? 'Saving...' : (isEditing ? 'Update & Publish' : 'Publish')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Editor */}
        <div className="lg:col-span-2 space-y-6">
          {/* Title */}
          <div>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Post title..."
              className="w-full text-3xl font-bold border-0 border-b-2 border-gray-200 focus:border-[#243185] focus:ring-0 outline-none py-3 px-1 placeholder-gray-300 transition-colors"
            />
            {slugPreview && (
              <p className="text-xs text-gray-400 mt-1.5 font-mono">
                nikabatravels.com/blogs/{slugPreview}
              </p>
            )}
          </div>

          {/* Excerpt */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Excerpt</label>
            <textarea
              value={formData.excerpt}
              onChange={(e) => setFormData(prev => ({ ...prev, excerpt: e.target.value }))}
              placeholder="Brief summary of the post (shown in blog feed)..."
              rows={3}
              maxLength={500}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#243185]/20 focus:border-[#243185] outline-none resize-none transition-all"
            />
            <p className="text-xs text-gray-400 text-right mt-1">{formData.excerpt.length}/500</p>
          </div>

          {/* Rich Text Editor */}
          <div>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <label className="block text-sm font-semibold text-gray-700">Content</label>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => imageHandler()}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-[#243185] rounded-lg hover:bg-[#1a2460] transition-colors shadow-sm"
                >
                  <FiImage className="w-3.5 h-3.5" />
                  Insert Image
                </button>
                <button
                  type="button"
                  onClick={() => setShowGridModal(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors shadow-sm"
                >
                  <FiGrid className="w-3.5 h-3.5" />
                  Insert Photo Grid
                </button>
                <button
                  type="button"
                  onClick={cleanBrokenImages}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                >
                  <FiTrash2 className="w-3.5 h-3.5" />
                  Clean Broken Images
                </button>
              </div>
            </div>
            <div className="border border-gray-300 rounded-lg bg-white blog-editor-wrapper relative">
              <ReactQuill
                ref={quillRef}
                theme="snow"
                value={formData.content}
                onChange={(value) => setFormData(prev => ({ ...prev, content: value }))}
                modules={modules}
                formats={formats}
                placeholder="Write your travel story..."
                style={{ minHeight: '400px' }}
              />
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Featured Image */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <FiImage className="w-4 h-4" />
              Featured Image
            </h3>
            {featuredImagePreview ? (
              <div className="relative group">
                <img
                  src={featuredImagePreview}
                  alt="Featured"
                  className="w-full h-40 object-cover rounded-lg border border-gray-200"
                />
                <button
                  onClick={() => { setFeaturedImage(null); setFeaturedImagePreview(''); }}
                  className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                >
                  <FiX className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center w-full border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-[#243185] hover:bg-[#243185]/5 transition-all">
                <FiImage className="w-8 h-8 text-gray-400 mb-2" />
                <span className="text-sm text-gray-500 mb-2">Upload Featured Image</span>
                <input
                  ref={featuredImageInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleFeaturedImageChange}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-[#243185] file:text-white hover:file:bg-[#1a2460] file:cursor-pointer"
                />
                <span className="text-xs text-gray-400 mt-2">JPG, PNG, WebP (max 5MB)</span>
              </div>
            )}
          </div>

          {/* Author */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-800 mb-3">Author</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Display Name</label>
                <input
                  type="text"
                  value={formData.authorName}
                  onChange={(e) => setFormData(prev => ({ ...prev, authorName: e.target.value }))}
                  placeholder="e.g. Nikaba Travels"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#243185]/20 focus:border-[#243185] outline-none transition-all"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Photo</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={formData.authorPhoto}
                    onChange={(e) => setFormData(prev => ({ ...prev, authorPhoto: e.target.value }))}
                    placeholder="URL or upload below"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#243185]/20 focus:border-[#243185] outline-none transition-all"
                  />
                  <label className="flex items-center gap-1 px-3 py-2 bg-[#243185] text-white text-xs font-medium rounded-lg cursor-pointer hover:bg-[#1a2460] transition-colors whitespace-nowrap">
                    <FiImage size={14} />
                    Upload
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        try {
                          toast.info('Uploading author photo...');
                          const fd = new FormData();
                          fd.append('image', file);
                          const res = await uploadBlogImage(fd);
                          const url = res.data?.data?.url || res.data?.url;
                          if (url) {
                            setFormData(prev => ({ ...prev, authorPhoto: url }));
                            toast.success('Author photo uploaded!');
                          } else {
                            toast.error('Upload succeeded but no URL returned');
                          }
                        } catch (err) {
                          console.error('Author photo upload error:', err);
                          if (err.response?.status === 401 || err.response?.status === 403) {
                            toast.error('Auth failed. Please log out and log back in.');
                          } else {
                            toast.error('Failed to upload: ' + (err.response?.data?.message || err.message));
                          }
                        }
                        e.target.value = '';
                      }}
                    />
                  </label>
                </div>
              </div>
              {(formData.authorName || formData.authorPhoto) && (
                <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                  {formData.authorPhoto ? (
                    <img src={formData.authorPhoto} alt="" className="w-8 h-8 rounded-full object-cover border border-gray-200" onError={(e) => { e.target.style.display = 'none'; }} />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#243185] to-[#5365dc] flex items-center justify-center text-white text-xs font-bold">
                      {formData.authorName?.charAt(0)?.toUpperCase() || 'N'}
                    </div>
                  )}
                  <span className="text-sm font-medium text-gray-700">{formData.authorName || 'Author'}</span>
                </div>
              )}
            </div>
          </div>

          {/* Category */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-800 mb-3">Category</h3>
            <select
              value={formData.category}
              onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#243185]/20 focus:border-[#243185] outline-none bg-white transition-all"
            >
              {CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          {/* Tags */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-800 mb-3">Tags</h3>
            <input
              type="text"
              value={formData.tags}
              onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
              placeholder="e.g. travel, india, adventure"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#243185]/20 focus:border-[#243185] outline-none transition-all"
            />
            <p className="text-xs text-gray-400 mt-1.5">Separate with commas</p>
            {formData.tags && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {formData.tags.split(',').map((tag, i) => (
                  tag.trim() && (
                    <span key={i} className="bg-[#243185]/10 text-[#243185] text-xs px-2.5 py-1 rounded-full font-medium">
                      {tag.trim()}
                    </span>
                  )
                ))}
              </div>
            )}
          </div>

          {/* SEO */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <button
              onClick={() => setShowSeoPanel(!showSeoPanel)}
              className="flex items-center justify-between w-full"
            >
              <h3 className="font-semibold text-gray-800">SEO Settings</h3>
              <span className="text-gray-400 text-sm">{showSeoPanel ? '▲' : '▼'}</span>
            </button>
            {showSeoPanel && (
              <div className="mt-4 space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">SEO Title ({formData.seoTitle.length}/70)</label>
                  <input
                    type="text"
                    value={formData.seoTitle}
                    onChange={(e) => setFormData(prev => ({ ...prev, seoTitle: e.target.value }))}
                    placeholder={formData.title || 'SEO optimized title...'}
                    maxLength={70}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#243185]/20 focus:border-[#243185] outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Meta Description ({formData.seoDescription.length}/160)</label>
                  <textarea
                    value={formData.seoDescription}
                    onChange={(e) => setFormData(prev => ({ ...prev, seoDescription: e.target.value }))}
                    placeholder="Describe this post for search engines..."
                    rows={3}
                    maxLength={160}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#243185]/20 focus:border-[#243185] outline-none resize-none transition-all"
                  />
                </div>
                {/* Google Preview */}
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-xs text-gray-400 mb-2 font-medium">Google Preview</p>
                  <p className="text-blue-700 text-sm font-medium truncate">
                    {formData.seoTitle || formData.title || 'Post Title'}
                  </p>
                  <p className="text-green-700 text-xs truncate mt-0.5">
                    nikabatravels.com/blogs/{slugPreview || 'post-slug'}
                  </p>
                  <p className="text-gray-600 text-xs mt-1 line-clamp-2">
                    {formData.seoDescription || formData.excerpt || 'Post description will appear here...'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Photo Grid Modal */}
      <PhotoGridModal
        isOpen={showGridModal}
        onClose={() => setShowGridModal(false)}
        onInsert={insertPhotoGrid}
      />

      {/* Editor Styles */}
      <style>{`
        .blog-editor-wrapper .ql-toolbar {
          border: none;
          border-bottom: 1px solid #e5e7eb;
          background: #f9fafb;
          border-radius: 0.5rem 0.5rem 0 0;
          padding: 8px 12px;
          position: sticky;
          top: 0;
          z-index: 10;
          box-shadow: 0 2px 4px rgba(0,0,0,0.04);
        }
        .blog-editor-wrapper .ql-container {
          border: none;
          font-size: 16px;
          line-height: 1.7;
          min-height: 400px;
          max-height: 70vh;
          overflow-y: auto;
        }
        .blog-editor-wrapper .ql-editor {
          padding: 20px 24px;
          min-height: 400px;
        }
        .blog-editor-wrapper .ql-editor.ql-blank::before {
          color: #9ca3af;
          font-style: normal;
        }
        .blog-editor-wrapper .ql-editor img {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
          margin: 8px 0;
          cursor: pointer;
          transition: box-shadow 0.2s;
          display: block;
        }
        .blog-editor-wrapper .ql-editor img[src=""],
        .blog-editor-wrapper .ql-editor img:not([src]) {
          display: none;
        }
        .blog-editor-wrapper .ql-editor img:hover {
          box-shadow: 0 0 0 3px rgba(36, 49, 133, 0.3);
        }
        .blog-editor-wrapper .ql-editor img[style*="float: left"] {
          margin-right: 16px;
          margin-bottom: 8px;
        }
        .blog-editor-wrapper .ql-editor img[style*="float: right"] {
          margin-left: 16px;
          margin-bottom: 8px;
        }
        .blog-editor-wrapper .ql-editor img[style*="display:inline-block"],
        .blog-editor-wrapper .ql-editor img[style*="display: inline-block"] {
          margin: 0;
        }
        /* Image resize module — corners + edges */
        .image-resizer {
          border: 2px dashed #243185 !important;
          position: relative;
        }
        .image-resizer .handle {
          background-color: #243185 !important;
          border: 2px solid white !important;
          width: 12px !important;
          height: 12px !important;
          border-radius: 50% !important;
          box-shadow: 0 1px 3px rgba(0,0,0,0.3) !important;
          z-index: 3 !important;
        }
        /* Make the resize border itself draggable (wider hit area) */
        .image-resizer::before {
          content: '';
          position: absolute;
          inset: -6px;
          cursor: nwse-resize;
          z-index: 1;
        }
        .blog-editor-wrapper .ql-editor h1 { font-size: 2em; font-weight: 700; margin: 1em 0 0.5em; }
        .blog-editor-wrapper .ql-editor h2 { font-size: 1.5em; font-weight: 600; margin: 0.8em 0 0.4em; }
        .blog-editor-wrapper .ql-editor h3 { font-size: 1.25em; font-weight: 600; margin: 0.6em 0 0.3em; }
        .blog-editor-wrapper .ql-editor blockquote {
          border-left: 4px solid #243185;
          padding-left: 16px;
          color: #4b5563;
          font-style: italic;
          margin: 16px 0;
        }
      `}</style>
    </div>
  );
};

export default BlogEditor;
